import { PromiseWorkerPool } from '@art-suite/art-core-ts';
import { FileCacheInterface } from '../../FileSystem';

export interface PreWarmItem {
  /** Local cache key (must uniquely identify the content — see FileCacheInterface.getFetchedFile). */
  fileName: string;
  /** URL to fetch on a cache miss. */
  url: string;
}

/**
 * - `pending`: created, waiting its turn in the one-at-a-time work queue.
 * - `active`: its turn came; freshening + downloading are underway.
 * - `done`: all work finished (the entry is retained until its last subscriber leaves).
 */
export type PreWarmPhase = 'pending' | 'active' | 'done';

export interface PreWarmStatus {
  key: string;
  phase: PreWarmPhase;
  /** Total items requested (`list.length`). */
  total: number;
  /** Items missing at the start — the ones actually downloaded. */
  toFetch: number;
  /** Of `toFetch`, how many have been attempted so far. */
  fetched: number;
  /** Already-cached + attempted — the numerator of `progress`. */
  completed: number;
  /** `completed / total`, clamped to [0, 1] (1 when `total` is 0). */
  progress: number;
  /** Convenience for `phase === "done"`. */
  done: boolean;
  /**
   * Whether the pre-warm PROCESS succeeded — not whether every fetch did. `done`
   * AND nothing is offline-missing. 404s / fetch failures do NOT count against it:
   * the process attempted them and there's nothing more it could do (a 404 photo
   * isn't on the server, period). The only thing that makes the process un-successful
   * is being offline without the files cached, because regaining connectivity would
   * let it finish the job. Equivalent to `done && !offlineIncomplete`.
   */
  success: boolean;
  /**
   * Items that aren't cached locally and couldn't be fetched because the device
   * is offline. 0 in the normal (online) path — downloads are attempted instead.
   */
  offlineMissing: number;
  /**
   * True when the batch finished `done` but some items are unavailable because the
   * device was offline (`offlineMissing > 0`). The fraction that IS available offline
   * is `progress`. Once `done`, this is exactly `!success` — it's just the named,
   * offline-specific framing of the same condition.
   */
  offlineIncomplete: boolean;
}

export type PreWarmSubscriber = (status: PreWarmStatus) => void;

export interface PreWarmCacheManagerOptions {
  /** Max concurrent downloads within a single key's work. */
  concurrency?: number;
  /** When true, emit `[PreWarmCache:<key>]` trace logging (lifecycle + per-key summary). */
  verbose?: boolean;
  /**
   * Returns the device's current offline status (read fresh each time work runs).
   * When offline, missing files are NOT fetched — the batch completes immediately
   * with whatever's already cached and reports the rest as `offlineMissing`
   * (`offlineIncomplete`). Defaults to always-online.
   */
  isOffline?: () => boolean;
}

const DEFAULT_CONCURRENCY = 5;

interface KeyEntry {
  key: string;
  list: PreWarmItem[];
  subscribers: Set<PreWarmSubscriber>;
  phase: PreWarmPhase;
  total: number;
  toFetch: number;
  fetched: number;
  completed: number;
  /** Misses that 404'd (photo not on server) — expected, handled silently. */
  notFound: number;
  /** Misses that failed for some other reason. */
  failed: number;
  /** Misses left un-fetched because the device was offline. */
  offlineMissing: number;
}

/**
 * Bulk-prefetches keyed lists of files into a {@link FileCacheInterface}.
 *
 * Per key (e.g. a scan UUID + its photos):
 *   - Only the FIRST subscribe for a live key starts work; later subscribes for
 *     the same key just attach and share its progress. The first list wins.
 *   - Subscribers get an immediate status, transition `pending → active` when
 *     the key's turn comes, and a final `done`. After `done`, no further updates.
 *   - Subscribers are OBSERVERS, not owners: once enqueued, the work always runs
 *     to completion — unsubscribing (e.g. the hook unmounting) never aborts it.
 *     The entry is cleaned up only once it's `done` AND has no subscribers; a
 *     later subscribe for a cleaned-up key starts fresh work (cheap — already
 *     cached items are skipped).
 *
 * Keys are processed ONE AT A TIME (a slow scan can't starve everything); within
 * a key, downloads run at `concurrency` (default 5) via {@link PromiseWorkerPool}.
 * Already-cached items are freshened (so the new downloads don't evict them) and
 * skipped — discovered in one `freshen` call that also returns the misses.
 */
export class PreWarmCacheManager {
  private readonly entries = new Map<string, KeyEntry>();
  // Serializes work across keys — one key's downloads run at a time.
  private workQueue: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly fileCache: FileCacheInterface,
    private readonly options: PreWarmCacheManagerOptions = {},
  ) {}

  private get concurrency(): number {
    return this.options.concurrency ?? DEFAULT_CONCURRENCY;
  }

  /** Current offline status (read fresh each time, so it tracks connectivity changes). */
  private get offline(): boolean {
    return this.options.isOffline?.() ?? false;
  }

  /**
   * Subscribe to pre-warming for `key`. Creates + enqueues the work if `key`
   * isn't already live; otherwise just attaches to the existing work (ignoring
   * `list`). Emits the current status immediately. Returns an unsubscribe fn;
   * it detaches the observer but never aborts in-flight work (the prep keeps
   * running and is cleaned up when it finishes with no subscribers left).
   */
  subscribe(key: string, list: PreWarmItem[], onStatus: PreWarmSubscriber): () => void {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = {
        key,
        list,
        subscribers: new Set(),
        phase: 'pending',
        total: list.length,
        toFetch: 0,
        fetched: 0,
        completed: 0,
        notFound: 0,
        failed: 0,
        offlineMissing: 0,
      };
      this.entries.set(key, entry);
      void this.startEntry(entry);
    }
    const liveEntry = entry;
    liveEntry.subscribers.add(onStatus);
    onStatus(this.snapshot(liveEntry)); // immediate status (pending, active, or done)

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      this.unsubscribe(liveEntry, onStatus);
    };
  }

  /** Current status for `key`, or `undefined` if no live entry. */
  getStatus(key: string): PreWarmStatus | undefined {
    const entry = this.entries.get(key);
    return entry ? this.snapshot(entry) : undefined;
  }

  private unsubscribe(entry: KeyEntry, onStatus: PreWarmSubscriber): void {
    entry.subscribers.delete(onStatus);
    // Never abort in-flight prep — the work runs to completion regardless. Only
    // a finished entry with no subscribers left is dead weight; clean it up.
    // (A still-running entry is cleaned up by runEntry when it completes.)
    if (entry.subscribers.size === 0 && entry.phase === 'done') this.entries.delete(entry.key);
  }

  /**
   * Fast-pass (OFF the one-at-a-time queue): if everything is already cached,
   * report `done` immediately so a freshly-mounted "% prep" display can show
   * 100% without waiting behind other active batches.
   *
   * Offline: we never hit the network, so there's nothing to queue — report what's
   * cached and mark the rest `offlineMissing` (still off the queue, instant).
   * Otherwise queue the downloads.
   */
  private async startEntry(entry: KeyEntry): Promise<void> {
    const missing = await this.freshenMisses(entry);
    if (missing && missing.length === 0) {
      entry.completed = entry.total;
      this.complete(entry, `fast-pass — all ${entry.total} already cached`);
      return;
    }
    if (this.offline) {
      // `missing` is null only on a freshen error — treat the whole batch as missing.
      this.completeOffline(entry, missing?.length ?? entry.total);
      return;
    }
    this.log(entry, `queued — ${entry.total} item(s)`);
    this.enqueueWork(entry);
  }

  /**
   * Freshen the entry's files (so fresh downloads don't evict already-cached hits)
   * and return the misses. Returns `null` on error — callers fall through to the
   * normal queued path rather than guessing presence.
   */
  private async freshenMisses(entry: KeyEntry): Promise<string[] | null> {
    if (entry.total === 0) return [];
    try {
      const { missingKeys } = await this.fileCache.freshen(entry.list.map(i => i.fileName));
      return missingKeys;
    } catch {
      return null;
    }
  }

  /** Finish a batch offline: `missingCount` items stay un-fetched; the rest are cached. */
  private completeOffline(entry: KeyEntry, missingCount: number): void {
    entry.toFetch = missingCount;
    entry.offlineMissing = missingCount;
    entry.completed = entry.total - missingCount;
    this.complete(
      entry,
      `offline — ${entry.completed} available, ${missingCount} not available offline`,
    );
  }

  private enqueueWork(entry: KeyEntry): void {
    // `.then(run, run)` so a prior key's failure can't break the chain; `run`
    // swallows its own errors to keep `workQueue` resolved.
    const run = () => this.runEntry(entry).catch(() => undefined);
    this.workQueue = this.workQueue.then(run, run);
  }

  private async runEntry(entry: KeyEntry): Promise<void> {
    // Runs to completion regardless of subscribers — prep is never aborted.
    entry.phase = 'active';
    this.notify(entry);

    // freshen present entries (so fresh downloads don't evict them) and learn the misses.
    const { missingKeys } = await this.fileCache.freshen(entry.list.map(i => i.fileName));

    const missing = new Set(missingKeys);
    const toFetch = entry.list.filter(i => missing.has(i.fileName));
    entry.toFetch = toFetch.length;
    entry.completed = entry.total - toFetch.length; // already-cached count
    this.log(entry, `active — ${entry.completed} already cached, ${entry.toFetch} to fetch`);
    this.notify(entry);

    if (toFetch.length > 0) {
      // Went offline while queued: don't attempt downloads that would just fail.
      if (this.offline) {
        this.completeOffline(entry, toFetch.length);
        return;
      }
      const pool = new PromiseWorkerPool(this.concurrency);
      for (const item of toFetch) {
        await pool.queue(async () => {
          try {
            await this.fileCache.getFetchedFile(item.fileName, item.url);
          } catch (error) {
            // Best-effort: a single failed item still advances progress so the
            // set can complete rather than stalling on one bad URL. A 404 just
            // means the photo isn't on the server — expected, handled silently.
            if ((error as { status?: number })?.status === 404) entry.notFound += 1;
            else {
              entry.failed += 1;
              this.log(entry, `fetch failed for ${item.fileName}: ${String((error as Error)?.message ?? error)}`);
            }
          } finally {
            entry.fetched += 1;
            entry.completed += 1;
            this.notify(entry);
          }
        });
      }
      await pool.end();
    }

    const ok = entry.fetched - entry.notFound - entry.failed;
    this.complete(entry, `done — ${ok} fetched, ${entry.total - entry.toFetch} already cached, ${entry.notFound} missing(404), ${entry.failed} failed`);
  }

  /** Mark an entry done, notify, and release it if nobody's observing anymore. */
  private complete(entry: KeyEntry, logMessage: string): void {
    entry.phase = 'done';
    this.log(entry, logMessage);
    this.notify(entry);
    if (entry.subscribers.size === 0) this.entries.delete(entry.key);
  }

  private log(entry: KeyEntry, message: string): void {
    if (this.options.verbose) console.log(`[PreWarmCache:${entry.key}] ${message}`);
  }

  private snapshot(entry: KeyEntry): PreWarmStatus {
    return {
      key: entry.key,
      phase: entry.phase,
      total: entry.total,
      toFetch: entry.toFetch,
      fetched: entry.fetched,
      completed: entry.completed,
      progress: entry.total === 0 ? 1 : Math.min(entry.completed / entry.total, 1),
      done: entry.phase === 'done',
      success: entry.phase === 'done' && entry.offlineMissing === 0,
      offlineMissing: entry.offlineMissing,
      offlineIncomplete: entry.offlineMissing > 0,
    };
  }

  private notify(entry: KeyEntry): void {
    const status = this.snapshot(entry);
    // Copy first — a subscriber may unsubscribe synchronously from its callback.
    for (const sub of [...entry.subscribers]) sub(status);
  }
}
